/** HTTP Client Library. */
import axios from 'axios';

import { operationNow } from 'shared/lib/dateFormatter';

const auth0API = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;
const awsAPI = process.env.REACT_APP_DEFAULT_API;
const slackWebhookUrl = process.env.REACT_APP_ERROR_DETECT_API;
const currentEnvironment = process.env.REACT_APP_NODE_ENV; // If currentEnvironment is not 'production', assume it's a test environment.

/** Default API call function.  */
export const callAPI = async ({
    method = 'get',
    module = null,
    component = null,
    type = null,
    condition = null,
    getAccessTokenSilently,
    email,
    obj = null,
    content_type = 'application/json',
    onUploadProgress = null,
    responseType = 'json',
    onInit = () => {},
    onSuccess = () => {},
    onError = () => {},
    onFinal = () => {},
}) => {
    try {
        onInit();
        // Method to retrieve an access token using Auth0, makes an authenticated API call to fetch shipment-related data,
        // and handles errors by throwing them for further processing.
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: auth0API, // Include the audience of authorization parameters to authenticate API call.
            },
        });
        const apiConfig = {
            method: method,
            url: awsAPI,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': content_type,
            },
            params: {
                module: module,
                component: component,
                type: type,
                condition: condition,
                email: email,
                test: currentEnvironment === 'production' ? false : true,
            },
            data: obj instanceof FormData ? obj : { data: obj },
            onUploadProgress: onUploadProgress,
            responseType: responseType,
        };
        const resp = await requestRetryingUnanswered(apiConfig);
        const error = isMissException(resp);
        if (error) {
            // A rejection the server chose to send — the user reads it and fixes their input.
            // If it had been a real failure the server would already have paged for it.
            onError(error.message ? new Error(error.message) : error);
            return;
        }
        if (resp.status >= 200 && resp.status < 300) {
            onSuccess(resp);
        }
    } catch (error) {
        onError(withServerMessage(error));
        if (shouldReportErrorToSlack(error)) {
            const report = {
                error,
                method,
                module,
                component,
                type,
                email,
                obj,
                content_type,
                responseType,
                service_name: 'Operations System',
            };
            if (isUnanswered(error)) {
                queueUnansweredFailureReport(report);
            } else {
                reportErrorToSlack(report).catch(() => {});
            }
        }
        console.error('API error', error);
    } finally {
        onFinal();
    }
};

const RETRY_DELAY_MS = 2000;
// When a user's connection blips, the screen fails within moments. When a screen failed only
// after a long wait, the server was too slow to answer — the browser cannot tell that apart from
// a lost connection — and asking again would leave the user waiting twice as long on a server
// that is already struggling.
const RETRY_ONLY_IF_FAILED_WITHIN_MS = 10000;

/** The user's screen got nothing back — their connection dropped, or the server never answered. */
const isUnanswered = (error) => {
    if (error?.isAxiosError !== true) return false;
    const status = error?.response?.status;
    return status == null || status < 100;
};

/**
 * Only loading a screen is retried for the user. A save that got no answer may already have gone
 * through, and saving it again could create a duplicate shipment or entry, so the user sees the
 * failure and decides for themselves.
 */
const isRetriedOnNoReply = (method, error) => method?.toLowerCase?.() === 'get' && isUnanswered(error);

/**
 * Gives a screen that failed to load one more try, two seconds later, when the user's connection
 * dropped for a moment.
 *
 * A Wi-Fi blip or a laptop waking up fails everything a screen was loading at once — opening the
 * Duty Calculator loads five lists together — even though the connection is back a moment later.
 * The second try lets the screen fill in without the user seeing an error and without paging an
 * operator who has nothing to fix. When the server did answer, even with an error, its answer
 * stands: asking again would only show the user the same error later.
 */
const requestRetryingUnanswered = async (apiConfig) => {
    const startedAt = Date.now();
    try {
        return await axios(apiConfig);
    } catch (error) {
        if (!isRetriedOnNoReply(apiConfig.method, error)) throw error;
        if (Date.now() - startedAt > RETRY_ONLY_IF_FAILED_WITHIN_MS) throw error;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        try {
            return await axios(apiConfig);
        } catch (retryError) {
            // Tells the operator this screen already had its second try and still could not load.
            retryError.retriedOnce = true;
            throw retryError;
        }
    }
};

/**
 * The error to hand the caller, carrying the sentence the server sent when there is one.
 *
 * A rejected request states its reason in the body (`{"message": "..."}`) — the import date is
 * missing, the name already exists — but on a 4xx the browser only sees axios' own
 * "Request failed with status code 400", which tells an operator nothing about what to fix.
 * That sentence is already pulled out for the Slack alert; this puts it in front of the person
 * who can act on it. The websocket screens never pass through here — they read `response.message`
 * off their own callback — so this only affects requests made with `callAPI`.
 *
 * Anything with no server sentence (the request died on the way, the session expired, an
 * onSuccess callback threw) is passed through untouched and keeps the message it arrived with.
 * `response` and `cause` stay attached so a caller can still reach the original failure.
 */
const withServerMessage = (error) => {
    const serverMessage = error?.response?.data?.message;
    if (typeof serverMessage !== 'string' || serverMessage === '') return error;

    const surfaced = new Error(serverMessage);
    surfaced.cause = error;
    surfaced.response = error.response;
    return surfaced;
};

/** Handler to detect api error case. */
export const isMissException = (response) => {
    let body = response?.body || response.data?.body || response.data?.['body-json']?.body;

    if (body && 'error' in body) return body;

    // The backend signals failures with a `{"message": "..."}` envelope, but API Gateway
    // hands those back to the browser as HTTP 200. Without this check the envelope is
    // treated as real data and reaches the caller's onSuccess, where it corrupts state
    // (e.g. MUI DataGrid throws on a row that has no `id`).
    return isErrorEnvelope(body) || isErrorEnvelope(response?.data) || false;
};

/** A response payload whose only field is a `message` string is always a backend error envelope. */
const isErrorEnvelope = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
    const keys = Object.keys(payload);
    if (keys.length !== 1 || keys[0] !== 'message' || typeof payload.message !== 'string') return false;
    return payload;
};

/**
 * When the failure happened, on the operations clock (LA) and labelled as such, so an on-call
 * reader sees one timeline no matter where the browser that hit the error was located.
 */
function getCurrentTime() {
    return operationNow().format('YYYY-MM-DD HH:mm:ss [PT]');
}

/**
 * Whether an operator needs to be paged about this failure from the browser.
 *
 * The server pages itself for anything it managed to handle, with the real exception attached,
 * so repeating it here would ring twice for one incident. What is left for the browser to
 * report is what the server never saw: the request died on the way, or the load balancer
 * answered alone because no instance could take it.
 *
 * Losing the session is not an incident. An expired login and a screen that crashed while
 * rendering a reply both surface here without a response attached, and neither is something an
 * operator can act on at 2am, so a failed request has to identify itself as one.
 *
 * A 503 the app itself answered (Auth0 did not respond in time) is told apart from one the load
 * balancer sent by its body: the app always answers with its `{ message }` envelope, the load
 * balancer with plain text, and a load-balancer reply without CORS headers never reaches here
 * with a status at all. The app has already paged with the real exception, so this one stays quiet.
 */
const shouldReportErrorToSlack = (error) => {
    const status = error?.response?.status;
    if (status == null || status < 100) return error?.isAxiosError === true;
    if (status === 503 && typeof error?.response?.data?.message === 'string') return false;
    return status >= 502;
};

/** Pulls out the sentence that tells an operator what went wrong, whatever shape it arrived in. */
const getErrorMessageForSlack = (error) => {
    const responseMessage = error?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage) return responseMessage;
    // Keep whatever the server said even when it is not a plain sentence — the generic
    // "Request failed with status code ..." that follows tells an operator nothing.
    if (responseMessage != null) {
        try {
            return JSON.stringify(responseMessage);
        } catch {
            // fall through to the error's own message
        }
    }
    if (typeof error?.message === 'string' && error.message) return error.message;
    try {
        return JSON.stringify(error) || 'Unknown error (no response)';
    } catch {
        return 'Unknown error (no response)';
    }
};

/**
 * Keeps one broken screen from burying every other alert in the channel.
 *
 * A user whose screen is failing keeps clicking, and each attempt fails the same way. Without a
 * ceiling that single problem fills the channel and hides the outage that started an hour
 * later. What gets held back is counted and reported with the next alert, so an operator still
 * sees how bad it got.
 */
const createAlertThrottle = (maxPerWindow, windowMs) => {
    let windowStart = 0;
    let sent = 0;
    let dropped = 0;

    return () => {
        const now = Date.now();
        let previouslyDropped = 0;
        if (now - windowStart >= windowMs) {
            previouslyDropped = dropped;
            dropped = 0;
            windowStart = now;
            sent = 0;
        }
        if (sent >= maxPerWindow) {
            dropped += 1;
            return { allowed: false, dropped: 0 };
        }
        sent += 1;
        return { allowed: true, dropped: previouslyDropped };
    };
};

const acquireAlertSlot = createAlertThrottle(10, 60000);

const UNANSWERED_ALERT_WINDOW_MS = 5000;
// Slack refuses the whole alert when this list runs past 3000 characters, as a bulk save can.
const FAILED_API_LIST_LIMIT = 2800;
// Slack also refuses the whole alert when a single field runs past 2000 characters. A Data
// Export request carries every column and every client the user picked, so its 504 was never
// reported and the outage went unnoticed. The clipped head is enough to see which screen and
// which request failed.
const SLACK_FIELD_LIMIT = 1500;

/** Shortens one alert field so Slack accepts the alert whatever the request carried. */
const clipForSlackField = (text) => {
    const value = String(text);
    return value.length > SLACK_FIELD_LIMIT ? `${value.slice(0, SLACK_FIELD_LIMIT)}…` : value;
};

/** What each user has lost in the current outage, waiting to go out to the operator as one alert. */
const pendingUnansweredFailures = new Map();

/**
 * Gathers everything one user failed to reach within a few seconds into a single alert for the
 * operator.
 *
 * When a user's connection drops on the Duty Calculator, its five lists all fail together, and
 * the operator used to get five alerts for one moment of lost Wi-Fi. These failures are still
 * reported rather than ignored: a server outage looks the same as a user going offline, and the
 * alert — how many users, which screens — is how the operator tells the two apart. If the user
 * keeps failing after the alert goes out, the next one follows, so a lasting outage is not hidden.
 */
const queueUnansweredFailureReport = (report) => {
    if (currentEnvironment !== 'production') return;

    const key = report.email ?? 'unknown';
    let batch = pendingUnansweredFailures.get(key);
    if (!batch) {
        batch = { firstFailedAt: getCurrentTime(), reports: [] };
        pendingUnansweredFailures.set(key, batch);
        setTimeout(() => {
            pendingUnansweredFailures.delete(key);
            reportUnansweredFailuresToSlack(batch).catch(() => {});
        }, UNANSWERED_ALERT_WINDOW_MS);
    }
    batch.reports.push(report);
};

/** One line per thing the user failed to load or save, so the operator can see which screen was hit. */
const describeFailedApi = ({ error, method, module, component, type, obj }) => {
    const parts = [
        `${method?.toUpperCase?.() ?? method}`,
        `module=${module}`,
        `component=${component}`,
        `type=${type}`,
        `— ${getErrorMessageForSlack(error)}`,
    ];
    if (error?.retriedOnce) parts.push('(retried once)');
    // A save is never retried, so this is the only record of what the user was trying to save.
    else if (obj != null && !(obj instanceof FormData)) parts.push(`obj=${JSON.stringify(obj)}`);
    return parts.join(' ');
};

/** Tells the operator that a user could not reach the server, and everything they failed to load or save. */
const reportUnansweredFailuresToSlack = async ({ firstFailedAt, reports }) => {
    const { allowed, dropped } = acquireAlertSlot();
    if (!allowed) return;

    try {
        const { email, service_name } = reports[0];
        const errorMessages = [...new Set(reports.map(({ error }) => getErrorMessageForSlack(error)))].join(', ');
        let failedApis = reports.map(describeFailedApi).join('\n');
        if (failedApis.length > FAILED_API_LIST_LIMIT) failedApis = `${failedApis.slice(0, FAILED_API_LIST_LIMIT)}…`;

        const payload = {
            blocks: [
                {
                    type: 'header',
                    text: {
                        type: 'plain_text',
                        text: `⚠️ ${email} got no response for ${reports.length} request(s). Please check the error.`,
                        emoji: true,
                    },
                },
                {
                    type: 'section',
                    fields: [
                        { type: 'mrkdwn', text: `*Error Message:*\n \`\`\`${errorMessages}\`\`\`` },
                        { type: 'mrkdwn', text: `*When*\n ${firstFailedAt}` },
                    ],
                },
                {
                    type: 'section',
                    fields: [
                        { type: 'mrkdwn', text: `*Service Name:*\n ${service_name}` },
                        { type: 'mrkdwn', text: `*Where:*\n ${window.location?.href ?? 'unknown'}` },
                    ],
                },
                {
                    type: 'section',
                    text: { type: 'mrkdwn', text: `*Failed APIs (${reports.length}):*\n\`\`\`${failedApis}\`\`\`` },
                },
            ],
        };

        if (dropped) {
            payload.blocks.push({
                type: 'context',
                elements: [{ type: 'mrkdwn', text: `${dropped} earlier alert(s) throttled in the last minute.` }],
            });
        }

        await axios.post(
            slackWebhookUrl,
            { payload },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: false,
                mode: 'cors',
            },
        );
    } catch (slackError) {
        console.error('Slack Webhook Error: ', slackError);
    }
};

/** Tells the operations channel that a user hit a failure the server could not report. */
const reportErrorToSlack = async (props) => {
    const { error, method, module, component, type, email, obj, content_type, responseType, service_name } = props;

    // Mixing errors from development into the channel makes an operator unable to tell which
    // ones reached a real user.
    if (currentEnvironment !== 'production') return;

    const { allowed, dropped } = acquireAlertSlot();
    if (!allowed) return;

    try {
        /** Structure of error report message. */
        const payload = {
            blocks: [
                {
                    type: 'header',
                    text: {
                        type: 'plain_text',
                        text: `⚠️ ${email} has created an error. Please check the error.`,
                        emoji: true,
                    },
                },
                {
                    type: 'section',
                    fields: [
                        {
                            type: 'mrkdwn',
                            text: `*Error Message:*\n \`\`\`${clipForSlackField(getErrorMessageForSlack(error))}\`\`\``,
                        },
                        {
                            type: 'mrkdwn',
                            text: `*When*\n ${getCurrentTime()}`,
                        },
                    ],
                },
                {
                    type: 'section',
                    fields: [
                        {
                            type: 'mrkdwn',
                            text: `*Method:*\n ${method?.toUpperCase?.() ?? method}`,
                        },
                        {
                            type: 'mrkdwn',
                            text: `*Module:*\n ${module}`,
                        },
                    ],
                },
                {
                    type: 'section',
                    fields: [
                        {
                            type: 'mrkdwn',
                            text: `*Component:*\n ${component}`,
                        },
                        {
                            type: 'mrkdwn',
                            text: `*Type:*\n ${type}`,
                        },
                    ],
                },
                {
                    type: 'section',
                    fields: [
                        {
                            type: 'mrkdwn',
                            text: `*Content Type:*\n ${content_type}`,
                        },
                        {
                            type: 'mrkdwn',
                            text: `*Response Type:*\n ${responseType}`,
                        },
                    ],
                },
                {
                    type: 'section',
                    fields: [
                        {
                            type: 'mrkdwn',
                            text: `*Service Name:*\n ${service_name}`,
                        },
                        {
                            type: 'mrkdwn',
                            text: `*Object:*\n \`\`\`${clipForSlackField(JSON.stringify(obj))}\`\`\``,
                        },
                    ],
                },
            ],
        };

        if (dropped) {
            payload.blocks.push({
                type: 'context',
                elements: [{ type: 'mrkdwn', text: `${dropped} earlier alert(s) throttled in the last minute.` }],
            });
        }

        await axios.post(
            slackWebhookUrl,
            { payload },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: false,
                mode: 'cors',
            },
        );
    } catch (slackError) {
        console.error('Slack Webhook Error: ', slackError);
    }
};

/** Tells the operations channel that a user is looking at a blank screen instead of their work. */
export const sendComponentErrorToSlack = async (error) => {
    // Mixing errors from development into the channel makes an operator unable to tell which
    // ones reached a real user.
    if (currentEnvironment !== 'production') return;

    // A screen that fails to draw is retried on every render, so it shares the same ceiling as
    // request failures rather than getting its own.
    const { allowed, dropped } = acquireAlertSlot();
    if (!allowed) return;

    try {
        const payload = {
            text: `Unexpected error has occured in render component.`,
            attachments: [
                {
                    // Without the screen it happened on, an operator cannot reproduce it.
                    pretext: `${error?.stack || error}`,
                    fields: [
                        { title: 'Where', value: window.location?.href ?? 'unknown', short: false },
                        { title: 'When', value: getCurrentTime(), short: true },
                        ...(dropped ? [{ title: 'Throttled', value: `${dropped} in last minute`, short: true }] : []),
                    ],
                },
            ],
        };

        await axios.post(
            slackWebhookUrl,
            { payload },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: false,
                mode: 'cors',
            },
        );
    } catch (slackError) {
        console.error('Slack Webhook Error: ', slackError);
    }
};
